import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { severityOptions, riskStatusOptions } from './constants';
import type { RiskSeverity, RiskStatus } from './constants';
import type { Tables } from '@/types/database.types';

type RiskRow = Tables<'risks'>;

export function RiskItem({
  risk,
  onUpdate,
  onDelete,
  readOnly = false
}: {
  risk: RiskRow;
  onUpdate: (id: string, updates: Partial<{ summary: string; description: string | null; severity: RiskSeverity; status: RiskStatus; identifiedDate: string | null; ownerName: string | null; mitigationPlan: string | null }>) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
}) {
  const [summary, setSummary] = useState(risk.summary);
  const [description, setDescription] = useState(risk.description || '');
  const [severity, setSeverity] = useState<RiskSeverity>(risk.severity as RiskSeverity);
  const [status, setStatus] = useState<RiskStatus>((risk.status as RiskStatus) || 'open');
  const [identifiedDate, setIdentifiedDate] = useState(risk.identified_date || '');
  const [ownerName, setOwnerName] = useState(risk.owner_name || '');
  const [mitigation, setMitigation] = useState(risk.mitigation_plan || '');

  const debouncedSummary = useDebounce(summary, 800);
  const debouncedDescription = useDebounce(description, 800);
  const debouncedOwnerName = useDebounce(ownerName, 800);
  const debouncedMitigation = useDebounce(mitigation, 800);
  const initialRef = useRef(true);

  useEffect(() => {
    if (!initialRef.current) {
      if (debouncedSummary !== risk.summary) {
        onUpdate(risk.id, { summary: debouncedSummary });
      }
    }
    initialRef.current = false;
  }, [debouncedSummary, risk.id, risk.summary, onUpdate]);

  useEffect(() => {
    if (debouncedDescription !== (risk.description || '')) {
      onUpdate(risk.id, { description: debouncedDescription || null });
    }
  }, [debouncedDescription, risk.id, risk.description, onUpdate]);

  useEffect(() => {
    if (debouncedOwnerName !== (risk.owner_name || '')) {
      onUpdate(risk.id, { ownerName: debouncedOwnerName || null });
    }
  }, [debouncedOwnerName, risk.id, risk.owner_name, onUpdate]);

  useEffect(() => {
    if (debouncedMitigation !== (risk.mitigation_plan || '')) {
      onUpdate(risk.id, { mitigationPlan: debouncedMitigation || null });
    }
  }, [debouncedMitigation, risk.id, risk.mitigation_plan, onUpdate]);

  const handleSeverityChange = (newSeverity: RiskSeverity) => {
    setSeverity(newSeverity);
    onUpdate(risk.id, { severity: newSeverity });
  };

  const handleStatusChange = (newStatus: RiskStatus) => {
    setStatus(newStatus);
    onUpdate(risk.id, { status: newStatus });
  };

  const handleDateChange = (newDate: string) => {
    setIdentifiedDate(newDate);
    onUpdate(risk.id, { identifiedDate: newDate || null });
  };

  const severityOption = severityOptions.find(s => s.value === severity);

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 group">
      <div className="flex items-start justify-between gap-3 mb-3">
        <Input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className="font-medium border-0 bg-transparent px-0 h-auto focus-visible:ring-0 flex-1"
          placeholder="Risk summary"
          readOnly={readOnly}
        />
        {!readOnly && (
          <button
            onClick={() => onDelete(risk.id)}
            className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Detailed description (optional)"
        className={cn(
          "w-full p-2 border border-input rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-2 focus:ring-ingesta-blue-500 focus:border-transparent text-sm mb-3",
          readOnly && "cursor-not-allowed opacity-60"
        )}
        readOnly={readOnly}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date Identified</label>
          <Input
            type="date"
            value={identifiedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            className="text-sm h-9"
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Owner</label>
          <Input
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Owner"
            className="text-sm h-9"
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <Select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value as RiskStatus)}
            containerClassName="w-full"
            className={cn("h-9", readOnly && "cursor-not-allowed opacity-60")}
            disabled={readOnly}
          >
            {riskStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Severity</label>
          <Select
            value={severity}
            onChange={(e) => handleSeverityChange(e.target.value as RiskSeverity)}
            containerClassName="w-full"
            className={cn("h-9", severityOption?.color, readOnly && "cursor-not-allowed opacity-60")}
            disabled={readOnly}
          >
            {severityOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Mitigation Plan</label>
        <textarea
          value={mitigation}
          onChange={(e) => setMitigation(e.target.value)}
          placeholder="Describe the mitigation strategy..."
          className={cn(
            "w-full p-2 border border-input rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-2 focus:ring-ingesta-blue-500 focus:border-transparent text-sm",
            readOnly && "cursor-not-allowed opacity-60"
          )}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}
